import { useEffect, useMemo, useRef, useState } from "react";
import { assembleStep, caseStepStates, evaluateStep, stepAsExercise } from "../lib/caseEngine";
import { abilityFor, hashSeed, pickDifficulty } from "../lib/difficulty";
import { vibrate } from "../lib/haptics";
import { NormTextList } from "./NormTextList";
import type { SoundService } from "../lib/sound";
import type { CaseExercise, Difficulty, LearningExercise, LearningState, ModeId } from "../types";

interface CasePanelProps {
  caseExercise: CaseExercise;
  state: LearningState;
  onAnswer: (
    stepExercise: LearningExercise,
    mode: ModeId,
    selected: number[],
    elapsedSeconds: number,
    difficulty: Difficulty
  ) => void;
  soundService?: SoundService;
  hapticsEnabled?: boolean;
}

const BURST_PARTICLES = Array.from({ length: 16 }, (_, index) => index);

export function CasePanel({ caseExercise, state, onAnswer, soundService, hapticsEnabled = true }: CasePanelProps) {
  const states = useMemo(() => caseStepStates(state, caseExercise), [state, caseExercise]);
  const firstOpen = states.findIndex((s) => s.status === "current");
  const [index, setIndex] = useState(firstOpen === -1 ? 0 : firstOpen);
  const [selected, setSelected] = useState<number | null>(null);
  const [revealed, setRevealed] = useState(false);
  const [startedAt] = useState(Date.now());
  const nextButtonRef = useRef<HTMLButtonElement | null>(null);

  const step = caseExercise.steps[index];
  const ability = abilityFor(state.abilities, caseExercise.skillTags);
  // Stufe pro Schritt aus dem Niveau (deterministisch per Seed, damit sie pro Schritt stabil bleibt).
  const difficulty = useMemo(
    () => pickDifficulty(ability, hashSeed(caseExercise.id, index)),
    [ability, caseExercise.id, index]
  );
  const assembled = useMemo(
    () => assembleStep(step, difficulty, hashSeed(caseExercise.id, index, difficulty)),
    [step, difficulty, caseExercise.id, index]
  );
  const result = revealed && selected !== null ? evaluateStep(assembled.answers, [selected]) : null;
  const isLast = index + 1 >= caseExercise.steps.length;

  useEffect(() => {
    if (!result || isLast) return;
    nextButtonRef.current?.focus();
  }, [isLast, result]);

  function choose(optionIndex: number) {
    if (revealed) return;
    soundService?.playCue("commit");
    vibrate(8, hapticsEnabled);
    const stepResult = evaluateStep(assembled.answers, [optionIndex]);
    soundService?.playCue(stepResult.correct ? "correct" : "wrong");
    setSelected(optionIndex);
    setRevealed(true);
    const elapsedSeconds = Math.max(8, Math.round((Date.now() - startedAt) / 1000));
    onAnswer(stepAsExercise(caseExercise, index, assembled), "case", [optionIndex], elapsedSeconds, difficulty);
  }

  function next() {
    if (isLast) return;
    setIndex(index + 1);
    setSelected(null);
    setRevealed(false);
  }

  return (
    <section className={`case-panel ${result?.correct ? "is-correct" : result ? "is-wrong" : ""}`}>
      {result?.correct ? <span className="reward-tick" aria-hidden="true" /> : null}
      {result?.correct ? (
        <span className="burst" aria-hidden="true">
          {BURST_PARTICLES.map((particle) => (
            <i key={particle} />
          ))}
        </span>
      ) : null}
      <div className="case-sachverhalt">
        <span className="eyebrow">Sachverhalt</span>
        <p>{caseExercise.sachverhalt}</p>
      </div>

      <ol className="case-stepper" aria-label="Prüfungsschritte">
        {caseExercise.steps.map((_, i) => (
          <li key={i} className={i === index ? "is-current" : states[i].status === "done" ? "is-done" : ""}>
            {i + 1}
          </li>
        ))}
      </ol>

      <div className="case-question-head">
        <h3 className="case-question">{step.question}</h3>
        <span className="question-count">Schritt {index + 1}/{caseExercise.steps.length}</span>
      </div>
      <div className="option-grid" aria-label="Antwortoptionen">
        {assembled.options.map((option, optionIndex) => {
          const isCorrect = revealed && assembled.answers.includes(optionIndex);
          const isWrong = revealed && selected === optionIndex && !assembled.answers.includes(optionIndex);
          return (
            <button
              className={`answer-option ${isCorrect ? "is-answer" : ""} ${isWrong ? "is-miss" : ""}`}
              disabled={revealed}
              key={optionIndex}
              onClick={() => choose(optionIndex)}
              type="button"
            >
              <span>{String.fromCharCode(65 + optionIndex)}</span>
              {option}
              {revealed && isCorrect ? <span className="opt-mark ok">✓</span> : null}
              {revealed && isWrong ? <span className="opt-mark miss">×</span> : null}
            </button>
          );
        })}
      </div>

      {result ? (
        <div className={`feedback-panel ${result.correct ? "is-correct" : "is-wrong"}`}>
          <strong>
            {result.correct
              ? "Sitzt."
              : `Nicht ganz - richtig ist: ${assembled.options.filter((_, i) => assembled.answers.includes(i)).join(" / ")}`}
          </strong>
          <p className="subsumtion">{step.explanation}</p>
          <div className="source-row">
            {step.sources.map((source) =>
              source.url ? (
                <a href={source.url} key={source.id} rel="noreferrer" target="_blank">
                  {source.label}
                </a>
              ) : (
                <span key={source.id}>{source.label}</span>
              )
            )}
          </div>
          <NormTextList sources={step.sources} />
          {isLast ? (
            <p className="case-done">Fall abgeschlossen.</p>
          ) : (
            <button className="primary-action" onClick={next} ref={nextButtonRef} type="button">
              Nächster Schritt →
            </button>
          )}
        </div>
      ) : null}
    </section>
  );
}
